import { CellClickedEvent, CellDoubleClickedEvent, CellKeyDownEvent, CellValueChangedEvent, ColDef, ColumnState, EditableCallbackParams, GetRowIdParams, RowClassParams, RowClickedEvent, ValueGetterParams } from "ag-grid-community";
import { MouseEvent, useCallback, useMemo } from "react";
import { AggridDiv, AggridInsertButton, AggridRemoveButton, commonBorderStyle, hoverColor, insertedColor, modifiedColor, selectedColor } from "src/components/common/common";
import { GridCustomColumnDefs } from "./GridCustomColumnDefs";
import { bottomCellTotalSetting } from "./GridCellStylesContent";
import { AgGridReact } from "ag-grid-react";
import { validateLeftAlignColumns, validateNumberLike } from "./gridStyleUtils";

interface GridSettingContentProps<TData = any> {
	gridRef: React.RefObject<AgGridReact<TData> | null>;
	key: string;
	gridData: TData[];
	defaultGridData: TData;
	selectedRow: TData | string[] | undefined;		// store의 selectedDto 데이터, Dto일수도 있고 string[]일수도 있음
	originalGridData?: TData[];
	updateRow?: (index: number, data: TData) => void;
}

export const useGridSettingContent = <TData extends Record<string, any> = any>(	props : GridSettingContentProps<TData>) => {

	const { 
		gridRef,
		key,
		gridData = [],					//Aggrid의 데이터(rowData 속성)
		defaultGridData = {},			//Aggrid의 각 행에 배정할 타입의 기본 데이터
		selectedRow,
		originalGridData = [],
		updateRow = () => {},
	} = props;

	
	// 각 행의 고유 ID를 반환하는 함수를 정의합니다., aggrid getRowId props에 배치해야함
	// Grid_Excel은 이걸 사용하면 안된다
	// Grid_Touch에서 사용해야함
	// 식별 id는 무조건 string로 고정
	const getRowId = useMemo(() => {
        return (params: GetRowIdParams<TData>) => String(params.data?.[key as keyof TData]);
    }, [key]);

    // TData는 그리드 행 데이터의 타입, TValue는 valueGetter가 반환할 값의 타입입니다.
	// ValueGetterParams 타입은 해당 라이브러리에서 사용하는 dto 개념이다
    const settingRowIndex = <
		TData = any, 
		TValue = any
	>(
		params: ValueGetterParams<TData, TValue>
	): TValue | string => {
        const pinned = params.node?.rowPinned;
        if (pinned === 'bottom') {
            return '';
        }

        // params.node.rowIndex는 number | undefined 일 수 있으므로 타입 가드 필요
        if (params.node && typeof params.node.rowIndex === 'number') {
            return (params.node.rowIndex + 1) as any; // TValue에 따라 string 또는 number일 수 있음
        }
        return '';
    }

    // 두 번째 열에 건수를 표시하는 함수
    const settingSecondColumnCount = <
		TData = any
	>(
		params: ValueGetterParams<TData>
	): string => {
        const pinned = params.node?.rowPinned;
        if (pinned === 'bottom') {
            return '';
        }

        // 두 번째 열인지 확인 (rowIndex가 1인 경우)
        if (params.node && typeof params.node.rowIndex === 'number' && params.node.rowIndex === 1) {
            return params.api.getDisplayedRowCount().toString();
        }
        return '';
    }

	// 데이터 패딩(행 개수 맞추기, 필요시)
	const paddedGridData = useMemo(() => {
		return Array.from({ length: gridData.length }, (_, index) => ({...(gridData[index] || defaultGridData), rowIndex: index}));
	}, [gridData, defaultGridData]) as TData[];

	const isNonEmptyFunction = (func: Function) => {
		const funcString = func.toString().replace(/\s/g, '');
		// 다양한 빈 함수 표현을 확인합니다.
		return funcString !== '()=>{}' && funcString !== 'function(){}' && funcString !== 'function(){}';
	};

	const defaultRowIndexCell = (params: any, isDeleted: boolean, isModified: boolean, isInserted: boolean) => {
		return (isDeleted ? 'D' : isModified ? 'U' : isInserted ? 'I' : params.node.rowIndex + 1);
	}

  const renderIndexCell = (
		params: any,
			//getRowStyleFlags 함수의 파라미터, .\gridStyleUtils.ts 문서 참고
			getRowStyleFlags : (params: any) => {
				isDeleted: boolean;
				isSelected: boolean;
				isInserted: boolean;
				isModified: boolean;
				isTransparent: boolean;
			},
			handleInsert: (rowIndex: number, event: React.MouseEvent) => void, // (e) 이벤트 대신 rowIndex를 받도록 수정
			handleRemove: (rowData: TData) => void
		) => {
		const { isDeleted, isSelected, isInserted, isModified, isTransparent } = getRowStyleFlags(params);
		const pinned = params.node.rowPinned;

		if (pinned === 'bottom') return <></>;

		const isHandleInsert = isNonEmptyFunction(handleInsert);

		return (
			<AggridDiv className="ag-row-cell-with-buttons"> 
				<span className={`ag-row-${isHandleInsert ? 'default' : 'normal'}-cell`}>
					{defaultRowIndexCell(params, isDeleted, isModified, isInserted)}
				</span>
				{isHandleInsert && (
					<span className="ag-row-hover-cell">
						<AggridInsertButton handleInsertClick={(e) => handleInsert(params.node.rowIndex, e)} />
						{params.data?.inserted && <AggridRemoveButton handleRemoveClick={() => handleRemove(params.data)} />}
					</span>
				)}
			</AggridDiv>
		);
	};
	

	/**
     * AG Grid의 rowClassRules를 동적으로 생성하는 공통 함수.
     * 'selected', 'deleted', 'inserted', 'modified', 'hover' 상태에 따라 CSS 클래스를 적용합니다.
     * @param options - 클래스 규칙 생성을 위한 설정 객체.
     * @param options.idKey - 각 행의 고유 식별자 필드명 (e.g., 'userId', 'itemCd').
     * @param options.selectedList - 선택된 행의 데이터 또는 ID 목록.
     * @param options.deletedList - 삭제된 행의 데이터 또는 ID 목록.
     * @param options.type - (선택 사항) 클래스 규칙 적용 대상. 'row' (기본값) 또는 'cell'. 지우지 말고 유지보수에 쓸것
     * @returns AG Grid에 적용할 수 있는 `rowClassRules` 객체.
    */
	const createRowClassRules = <TData extends { [key: string]: any; inserted?: boolean, isModified?: boolean }>(options: {
		idKey: keyof TData;
		selectedList: (TData | string | number)[];
		deletedList: (TData | string | number)[];
		headerNames?: Record<string, string>;
		type?: 'row' | 'cell';
	}) => {
		const { 
			idKey, 
			selectedList, 
			deletedList, 
			headerNames,
			type = 'row' 
		} = options;

		const isIdInList = (id: any, list: (TData | string | number)[] | undefined): boolean => {
			if (!list || list.length === 0) return false;
			// 리스트가 객체 배열인지, 아니면 ID(string/number) 배열인지 확인
			if (typeof list[0] === 'object' && list[0] !== null) {
				return (list as TData[]).some(item => item[idKey] === id);
			}
			return list.includes(id);
		};

		const rules: Record<string, (params: RowClassParams<TData>) => boolean> = {
			//disabled 적용시키고 싶으면 dto에 disabled: boolean 추가하고 적용시키고 싶은 컬럼의 disable 속성에 true 추가
			'disabled': (params) => {
				if (!params.data) return false;
				return !!params.data?.disabled;
			},
			'selected': (params) => {
				if (!params.data) return false;
				return isIdInList(params.data[idKey], selectedList);
			},
			'deleted': (params) => {
				if (!params.data) return false;
				return isIdInList(params.data[idKey], deletedList);
			},
			'inserted': (params) => {
				return !!params.data?.inserted;
			},
			'modified': (params) => {
				if (!params.data || !params.data.isModified) return false;
				if (isIdInList(params.data[idKey], deletedList) || params.data.inserted) {
					return false;
				}
				return true;
			},
			'hover': (params) => {
				if (!params.data) return false;
				const id = params.data[idKey];
				const isSelected = isIdInList(id, selectedList);
				const isDeleted = isIdInList(id, deletedList);
				const isInserted = !!params.data.inserted;
				// isModified도 데이터 플래그를 직접 확인합니다.
				const isModified = !!params.data.isModified;

				return !isSelected && !isDeleted && !isInserted && !isModified;
			}
		};
		
		return rules;
	};

	/**
	 * AG Grid의 cellClassRules를 생성하는 공통 함수입니다.
	 * defaultColDef에 적용하여 모든 컬럼(셀)에 공통 스타일 규칙을 적용할 때 사용합니다.
	 * disabled-cell 부여 조건 : 해당 셀에 '직접입력으로' 수정을 가할수 없을때
	 * pointer-cursor 부여 조건 : 해당 셀에 '클릭할때' 이벤트가 있을때(모달 창 열기 말고 input 표시 여부도 이벤트에 포함)
	 */
	const createCellClassRules = <TData extends { [key: string]: any; inserted?: boolean, isModified?: boolean }>(options: {
		idKey: keyof TData;
		selectedList: (TData | string | number)[];
		deletedList: (TData | string | number)[];
		disabledInsertedColumns: string[];
		disabledColumns: string[];
		pointerInsertedColumns: string[];
		pointerColumns: string[];
		alignLeftColumns?: string[]; // 추가: 왼쪽 정렬을 강제할 컬럼 목록
	}) => {
		const { 
				idKey,
				selectedList, deletedList, 
				disabledInsertedColumns, disabledColumns,
				pointerInsertedColumns, pointerColumns,
				alignLeftColumns // 추가
			} = options;

		const isIdInList = (id: any, list: (TData | string | number)[] | undefined): boolean => {
			if (!list || list.length === 0) return false;
			if (typeof list[0] === 'object' && list[0] !== null) {
				return (list as TData[]).some(item => item[idKey] === id);
			}
			return list.includes(id);
		};

		// App.css에 정의된 셀 스타일 클래스명 사용 (.selected-cell, .deleted-cell 등)
		return {
			'selected-cell': (params: any) => {
				if (!params.data) return false;
				return isIdInList(params.data[idKey], selectedList);
			},
			'deleted-cell': (params: any) => {
				if (!params.data) return false;
				return isIdInList(params.data[idKey], deletedList);
			},
			'inserted-cell': (params: any) => {
				return !!params.data?.inserted;
			},
			'modified-cell': (params: any) => {
				if (!params.data || !params.data.isModified) return false;
				// 삭제되거나 추가된 행은 수정 상태로 표시하지 않음
				if (isIdInList(params.data[idKey], deletedList) || params.data.inserted) {
					return false;
				}
				return true;
			},
			'disabled-cell': (params: any) => {
				if (!params.data) return false;
				if (params.node.rowPinned === 'bottom') return false;

				const colId = params.colDef.colId || params.colDef.field;

				// 추가된 행(inserted)일 때 비활성화 컬럼 체크
				if (params.data.inserted) {
					return disabledInsertedColumns?.includes(colId) || false;
				}
				
				// 일반 행일 때 비활성화 컬럼 체크
				return disabledColumns?.includes(colId) || false;
			},
			'pointer-cursor': (params: any) => {
				if (!params.data) return false;
				const colId = params.colDef.colId || params.colDef.field;

				if (params.data.inserted) {
					return pointerInsertedColumns?.includes(colId) || false;
				}

				return pointerColumns?.includes(colId) || false;
			},
			'text-align-center': (params: any) => {
				if (!params.data) return false;
				const colId = params.colDef.colId || params.colDef.field;
				if (validateLeftAlignColumns(colId, alignLeftColumns || []) && params.node.rowPinned !== 'bottom') return false;
				
				if (params.node.rowPinned === 'bottom') {
                    if(validateNumberLike(params.value)) return true;
					return true;
				}
				return false;
			},
			'text-align-right': (params: any) => {
				if (!params.data) return false;
				const colId = params.colDef.colId || params.colDef.field;

				if (validateLeftAlignColumns(colId, alignLeftColumns || []) && params.node.rowPinned !== 'bottom') return false;
				if(validateNumberLike(params.value)) return true;
				return false;
			}
		};
	};

	const defaultRowIndexCellButton = (
		handleInsertClick: (e: MouseEvent<HTMLButtonElement>) => void,
		handleRemoveClick: (e: MouseEvent<HTMLButtonElement>) => void,
		foundData: { rowIndex: number, valueIndex: number, orderIndex: number, inserted: boolean,  }, // dto 정보 { inserted: boolean }
	) => {
		return (
			<>
				<AggridInsertButton handleInsertClick={handleInsertClick} />
				{foundData?.inserted && <AggridRemoveButton handleRemoveClick={handleRemoveClick} />}
			</>
		)
	}

	//isHovered, isDeleted, isModified 구분한다음 rowindex의 버튼 표시하는 컴포넌트
	const rowIndexCellButtonType1 = (
		params: any, // 파라미터로 받은 현재 dto
		isInserted: boolean,
		isDeleted: boolean,
		isHovered: boolean,
		isModified: boolean,
		handleInsertClick: (e: MouseEvent<HTMLButtonElement>) => void,
		handleRemoveClick: (e: MouseEvent<HTMLButtonElement>) => void,
		foundData: any, // rowindex가 맞는 dto 정보, 없으면 null값이 들어옴 { inserted: boolean }
	) => {
		return (
			<AggridDiv>
				{!isDeleted && isHovered ? (
					defaultRowIndexCellButton(handleInsertClick, handleRemoveClick, foundData)
				) : (
					isInserted ? 'I' : isDeleted ? 'D' : isModified ? 'U' : params.node.rowIndex + 1
				)}
			</AggridDiv>
		)
	}

	//isHovered, isDeleted, 구분한다음 rowindex의 버튼 표시하는 컴포넌트
	const rowIndexCellButtonType2 = (
		params: any, // 파라미터로 받은 현재 dto
		isDeleted: boolean,
		isHovered: boolean,
		handleInsertClick: (e: MouseEvent<HTMLButtonElement>) => void,
		handleRemoveClick: (e: MouseEvent<HTMLButtonElement>) => void,
		foundData: any, // rowindex가 맞는 dto 정보, 없으면 null값이 들어옴 { inserted: boolean }
	) => {
		return (
			<AggridDiv>
				{!isDeleted && isHovered ? (
					defaultRowIndexCellButton(handleInsertClick, handleRemoveClick, foundData)
				) : (
					isDeleted ? 'D' : params.node.rowIndex + 1
				)}
			</AggridDiv>
		)
	}


	const handleSwitchButtonHandler = useCallback((
		valueIndex: number,
		params: any,
		newValue: 'Y' | 'N',
		headerNames: Record<string, string>
	) => {
		if (!originalGridData || !updateRow) {
			console.error("`handleSwitchButtonChange` requires `originalGridData` and `updateRow` to be provided to `useGridSettingContent` hook.");
			return;
		}

		const field = params.colDef.field as keyof TData;
		if (!field) {
			console.error("`field` is not defined in column definition.", params.colDef);
			return;
		}

		let updatedData = { ...params.data, [field]: newValue, };
		if(params.data.inserted === true) {
			updateRow(valueIndex, updatedData);
			return;
		}else{
			const originalRow = originalGridData.find(item => item.valueIndex === valueIndex);
			if (!originalRow) {
				console.error(`Original data not found for value index: ${valueIndex}`);
				return;
			}
			
			let updatedData = { ...params.data, [field]: newValue, };
	
			// 스위치 버튼으로 변경된 row값과, fetchdata로 불러온 값을 비교할 키 배열
			// 실제 row 데이터가 headerNames에 포함되어 있는 키의 값만 비교한다.
			const compareKeys: string[] = [];
			Object.keys(headerNames).forEach((key) => {
				compareKeys.push(key);
			});
	
			const cleanUpdateData: { [key: string]: any } = {};		//스위치 버튼 조작으로 업데이트된row값
			const cleanOriginalData: { [key: string]: any } = { rowIndex: updatedData.rowIndex };	//fetch로 받아온 값
			
			compareKeys.forEach(key => {
				
				if (key in updatedData) cleanUpdateData[key] = updatedData[key];
				if (key in originalRow) cleanOriginalData[key] = originalRow[key];
			});
			
			// console.log("updatedData.inserted?: ", updatedData.inserted);
			// console.log("cleanUpdateData?: ", JSON.stringify(cleanUpdateData));
			// console.log("cleanOriginalData?: ", JSON.stringify(cleanOriginalData));
			
			// 객체를 문자열로 변환하여 비교
			const isEqual = JSON.stringify(cleanUpdateData) === JSON.stringify(cleanOriginalData);

			// console.log("isEqual?: ", isEqual);
			
			if (!isEqual && updatedData.inserted === false) {
				updatedData = { ...updatedData, isModified: true };
			} else {
				updatedData = { ...updatedData, isModified: false };
			}
			
			updateRow(valueIndex, updatedData);

		}
		
	}, [originalGridData, updateRow]);

	
	//Grid_Touch에서 사용하는 함수, 정렬 후 조회 클릭시 선택된 행으로 스크롤을 이동시킴
	const ensureSelectedRow = useCallback(() => {
		
		if (gridRef?.current?.api && selectedRow && key && (selectedRow as TData)[key]) {
			//getRowNode 메서드를 사용하면 현재 그리드에 표시된 (정렬 및 필터링이 적용된) 행의 정보를 직접 가져온다
			const rowNode = gridRef.current.api.getRowNode((selectedRow as TData)[key]);
			if (rowNode && rowNode.rowIndex !== null) {
				// 그리드가 새로운 행을 렌더링할 시간을 주기 위해 setTimeout 사용
				setTimeout(() => {
					// gridRef.current?.api.ensureIndexVisible(rowNode.rowIndex!, 'middle');
				}, 0);
			}
		}
	}, [gridRef, selectedRow, key]);

    return {
		getRowId,
        settingRowIndex,
        settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
    }
}